import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import './AdminOrderDetail.css';
import { CreditCardIcon, BankIcon, DollarIcon, EditIcon, PackageIcon, CopyIcon, PrinterIcon, LinkIcon, CheckCircleIcon, ClockIcon, MailSendIcon, CheckIcon, UsersIcon, TruckIcon } from '../../components/SvgIcons';
import AedPrice from '../../components/AedPrice';

const AdminOrderDetail = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [showCustomerOrders, setShowCustomerOrders] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedPaymentId, setCopiedPaymentId] = useState(false);
  const [copiedOrderId, setCopiedOrderId] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [checkingRefund, setCheckingRefund] = useState(false);
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);
  const [showPaymentStatusMenu, setShowPaymentStatusMenu] = useState(false);
  const [updatingPayment, setUpdatingPayment] = useState(false);
  const [sendEmailOnShip, setSendEmailOnShip] = useState(true);
  const [showShipConfirm, setShowShipConfirm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [whatsappName, setWhatsappName] = useState('');
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [orderNote, setOrderNote] = useState('');
  const [noteLastUpdated, setNoteLastUpdated] = useState(null);
  const [noteUpdatedBy, setNoteUpdatedBy] = useState(null);
  const [editingNote, setEditingNote] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingItems, setEditingItems] = useState(false);
  const [editedQuantities, setEditedQuantities] = useState({});
  const [savingItems, setSavingItems] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [newItems, setNewItems] = useState([]);
  const [deliveryFeeOptions, setDeliveryFeeOptions] = useState([]);
  const [editingShipping, setEditingShipping] = useState(false);
  const [editedShipping, setEditedShipping] = useState(0);
  const [editedPayToDriver, setEditedPayToDriver] = useState(false);
  const [savingShipping, setSavingShipping] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [editedAddress, setEditedAddress] = useState({ fullName: '', phone: '', address: '', city: '', emirate: '' });
  const [savingAddress, setSavingAddress] = useState(false);
  const [whatsappTemplate, setWhatsappTemplate] = useState('');
  const [whatsappDriverTemplate, setWhatsappDriverTemplate] = useState('');
  const statusMenuRef = useRef(null);
  const paymentMenuRef = useRef(null);
  const { token } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusMenuRef.current && !statusMenuRef.current.contains(event.target)) {
        setShowStatusMenu(false);
      }
      if (paymentMenuRef.current && !paymentMenuRef.current.contains(event.target)) {
        setShowPaymentStatusMenu(false);
      }
    };

    if (showStatusMenu || showPaymentStatusMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusMenu, showPaymentStatusMenu]);

  useEffect(() => {
    fetchOrder();
    fetchWhatsappTemplate();
  }, [id]);

  const fetchWhatsappTemplate = async () => {
    try {
      const response = await fetch('/api/settings', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const settings = await response.json();
        if (settings.whatsappTemplate) setWhatsappTemplate(settings.whatsappTemplate);
        if (settings.whatsappDriverTemplate) setWhatsappDriverTemplate(settings.whatsappDriverTemplate);
        // Load delivery fee options
        if (settings.deliveryFeeOptions) {
          try {
            const opts = JSON.parse(settings.deliveryFeeOptions);
            if (Array.isArray(opts) && opts.length > 0) setDeliveryFeeOptions(opts.map(Number));
          } catch(e) {}
        } else if (settings.deliveryFee) {
          setDeliveryFeeOptions([Number(settings.deliveryFee) || 30]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchOrder = async () => {
    try {
      const response = await fetch(`/api/orders/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setOrder(data);
        setCustomerPhone(data.customerPhone || data.shippingAddress?.phone || '');
        setCustomerEmail(data.customerEmail || '');
        setWhatsappName(data.whatsappName || '');
        // Parse admin note (single note per order)
        if (data.adminNotes) {
          try {
            const noteData = JSON.parse(data.adminNotes);
            // Handle both old array format and new single note format
            if (Array.isArray(noteData)) {
              // Old format - take the last note if exists
              if (noteData.length > 0) {
                const lastNote = noteData[noteData.length - 1];
                setOrderNote(lastNote.text || '');
                setNoteLastUpdated(lastNote.createdAt || null);
                setNoteUpdatedBy(lastNote.createdBy || null);
              }
            } else {
              // New format - single note object
              setOrderNote(noteData.text || '');
              setNoteLastUpdated(noteData.updatedAt || null);
              setNoteUpdatedBy(noteData.updatedBy || null);
            }
          } catch (e) {
            setOrderNote('');
            setNoteLastUpdated(null);
            setNoteUpdatedBy(null);
          }
        }
        // Fetch customer order history - prioritize phone number, fallback to userId
        const phone = data.customerPhone || data.shippingAddress?.phone;
        if (phone) {
          fetchCustomerOrdersByPhone(phone);
        } else if (data.userId) {
          fetchCustomerOrders(data.userId);
        }
      }
    } catch (error) {
      console.error('Failed to fetch order:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerOrders = async (userId) => {
    try {
      const response = await fetch(`/api/orders/customer/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCustomerOrders(data);
      }
    } catch (error) {
      console.error('Failed to fetch customer orders:', error);
    }
  };

  const fetchCustomerOrdersByPhone = async (phone) => {
    try {
      const response = await fetch(`/api/orders/by-phone/${encodeURIComponent(phone)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCustomerOrders(data);
      }
    } catch (error) {
      console.error('Failed to fetch customer orders by phone:', error);
    }
  };

  const haptic = () => {
    try { navigator.vibrate && navigator.vibrate(10); } catch(e) {}
  };

  const copyAddress = () => {
    if (order?.shippingAddress) {
      const addr = order.shippingAddress;
      const fullAddress = `${addr.fullName}\n${addr.phone}\n${addr.address}\n${addr.city}, ${addr.emirate}`;
      navigator.clipboard.writeText(fullAddress);
      haptic();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const printAddressLabel = () => {
    if (!order?.shippingAddress) return;
    const addr = order.shippingAddress;
    const phone = addr.phone || order.customerPhone || '';
    const emirate = formatEmirate(addr.emirate);

    // Format phone for WhatsApp
    let waPhone = phone.replace(/[^0-9]/g, '');
    if (waPhone.startsWith('05') && waPhone.length === 10) {
      waPhone = '971' + waPhone.substring(1);
    } else if (waPhone.startsWith('00971')) {
      waPhone = waPhone.substring(2);
    }
    const whatsappUrl = `https://wa.me/${waPhone}`;
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(whatsappUrl)}`;

    // Arabic emirate names
    const EMIRATE_AR = {
      'abu_dhabi': 'أبوظبي',
      'dubai': 'دبي',
      'sharjah': 'الشارقة',
      'al_ain': 'العين',
      'ras_al_khaimah': 'رأس الخيمة',
      'ajman': 'عجمان',
      'fujairah': 'الفجيرة',
      'umm_al_quwain': 'أم القيوين'
    };
    const emirateAr = EMIRATE_AR[addr.emirate] || '';

    const labelHtml = `
      <!DOCTYPE html>
      <html dir="ltr">
      <head>
        <title>Shipping Label - ${order.id}</title>
        <style>
          @page { size: 4in 6in; margin: 0; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: Arial, sans-serif;
            padding: 10px;
            width: 4in;
            height: 6in;
          }
          .label {
            border: 2px solid #000;
            padding: 12px;
            height: 100%;
            display: flex;
            flex-direction: column;
          }
          .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
            margin-bottom: 12px;
          }
          .phone-section {
            text-align: left;
            direction: ltr;
          }
          .phone-label {
            font-size: 7px;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 2px;
          }
          .phone-number {
            font-size: 24px;
            font-weight: bold;
            direction: ltr;
            text-align: left;
          }
          .emirate {
            font-size: 16px;
            font-weight: bold;
            margin-top: 4px;
          }
          .emirate-ar {
            font-size: 14px;
            font-weight: bold;
            margin-top: 2px;
            direction: rtl;
          }
          .qr-section {
            text-align: center;
          }
          .qr-code {
            width: 70px;
            height: 70px;
          }
          .qr-label {
            font-size: 7px;
            color: #666;
            margin-top: 3px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 3px;
          }
          .wa-icon {
            width: 11px;
            height: 11px;
          }
          .name {
            font-size: 26px;
            font-weight: bold;
            margin-bottom: 10px;
            text-align: center;
            unicode-bidi: plaintext;
          }
          .address {
            font-size: 18px;
            line-height: 1.6;
            flex: 1;
            text-align: center;
            unicode-bidi: plaintext;
          }
          .order-items {
            border-top: 1px dashed #999;
            padding-top: 8px;
            margin-top: auto;
            direction: ltr;
            text-align: left;
          }
          .order-item-line {
            font-size: 14px;
            font-weight: bold;
            margin-bottom: 3px;
          }
          .footer {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-top: 2px solid #000;
            padding-top: 10px;
            margin-top: 10px;
            direction: ltr;
          }
          .brand {
            font-size: 13px;
            font-weight: bold;
            color: #333;
          }
          .order-id {
            font-size: 11px;
            color: #666;
          }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .footer { border-top: 2px solid #000 !important; }
            .header { border-bottom: 2px solid #000 !important; }
            .order-items { border-top: 1px dashed #999 !important; }
          }
        </style>
      </head>
      <body>
        <div class="label">
          <div class="header">
            <div class="phone-section">
              <div class="phone-label">Phone</div>
              <div class="phone-number">${phone}</div>
              <div class="emirate">${emirate.toUpperCase()}</div>
              ${emirateAr ? `<div class="emirate-ar">${emirateAr}</div>` : ''}
            </div>
            <div class="qr-section">
              <img class="qr-code" src="${qrCodeUrl}" alt="WhatsApp QR" />
              <div class="qr-label">
              <svg class="wa-icon" viewBox="0 0 24 24" fill="#000">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp
            </div>
            </div>
          </div>
          <div class="name">${addr.fullName}</div>
          <div class="address">
            ${addr.address}<br>
            ${addr.city}
          </div>
          <div class="order-items">
            <div style="font-size:11px;color:#666;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px;">Items:</div>
            ${order.items.map(item => `<div class="order-item-line">${item.quantity}x ${item.name}</div>`).join('')}
          </div>
          <div class="footer">
            <div class="brand">web.ae</div>
            <div class="order-id">Order: ${order.id}</div>
          </div>
        </div>
      </body>
      </html>
    `;

    // Use hidden iframe instead of new tab
    let printFrame = document.getElementById('print-label-frame');
    if (printFrame) printFrame.remove();
    printFrame = document.createElement('iframe');
    printFrame.id = 'print-label-frame';
    printFrame.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:4in;height:6in;border:none;';
    document.body.appendChild(printFrame);
    const frameDoc = printFrame.contentDocument || printFrame.contentWindow.document;
    frameDoc.open();
    frameDoc.write(labelHtml);
    frameDoc.close();

    let printed = false;
    const triggerPrint = () => {
      if (printed) return;
      printed = true;
      printFrame.contentWindow.focus();
      printFrame.contentWindow.print();
    };

    // Wait for QR code to load then print
    const img = frameDoc.querySelector('.qr-code');
    if (img && img.complete) {
      triggerPrint();
    } else if (img) {
      img.onload = triggerPrint;
      img.onerror = triggerPrint;
      setTimeout(triggerPrint, 3000);
    } else {
      triggerPrint();
    }

    // Auto-mark as printed
    if (!order.printed) {
      fetch(`/api/orders/${order.id}/printed`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      }).then(res => res.json()).then(updated => {
        setOrder(prev => ({ ...prev, printed: updated.printed, status: updated.status }));
      }).catch(() => {});
    }
  };

  const copyPaymentLink = () => {
    const link = `https://web.ae/pay/${order.id}`;
    navigator.clipboard.writeText(link);
    haptic();
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const saveCustomerInfo = async () => {
    setSavingCustomer(true);
    try {
      const response = await fetch(`/api/orders/${id}/customer`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          customerPhone: customerPhone,
          customerEmail: customerEmail,
          whatsappName: whatsappName
        })
      });

      if (response.ok) {
        setOrder(prev => ({
          ...prev,
          customerPhone: customerPhone,
          customerEmail: customerEmail,
          whatsappName: whatsappName
        }));
        setEditingCustomer(false);
      }
    } catch (error) {
      console.error('Failed to update customer info:', error);
    } finally {
      setSavingCustomer(false);
    }
  };

  const saveNote = async () => {
    setSavingNote(true);
    try {
      const response = await fetch(`/api/orders/${id}/note`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ note: orderNote })
      });

      if (response.ok) {
        const data = await response.json();
        setNoteLastUpdated(data.note.updatedAt);
        setNoteUpdatedBy(data.note.updatedBy);
        // Update order.adminNotes so cancel works correctly
        setOrder(prev => ({
          ...prev,
          adminNotes: JSON.stringify(data.note)
        }));
        setEditingNote(false);
      }
    } catch (error) {
      console.error('Failed to save note:', error);
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteOrder = async () => {
    setDeleting(true);
    try {
      const response = await fetch(`/api/orders/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        navigate('/dashboard4/orders');
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to delete order');
      }
    } catch (error) {
      console.error('Failed to delete order:', error);
      alert('Failed to delete order');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const startEditItems = () => {
    const qtys = {};
    order.items.forEach(item => { qtys[item.productId] = item.quantity; });
    setEditedQuantities(qtys);
    setNewItems([]);
    setEditingItems(true);
    // Fetch products for add dropdown
    fetch('/api/products').then(r => r.json()).then(data => setAllProducts(data)).catch(() => {});
  };

  const changeEditQty = (productId, delta) => {
    // Check if it's a new item
    const isNew = newItems.some(ni => ni.productId === productId);
    if (isNew) {
      setNewItems(prev => prev.map(ni => ni.productId === productId ? { ...ni, quantity: Math.max(0, ni.quantity + delta) } : ni).filter(ni => ni.quantity > 0));
    } else {
      setEditedQuantities(prev => ({
        ...prev,
        [productId]: Math.max(0, (prev[productId] || 1) + delta)
      }));
    }
  };

  const addNewItem = (productId) => {
    if (!productId) return;
    const product = allProducts.find(p => p.id === productId);
    if (!product) return;
    // Check if already in order (existing or new)
    const existingInOrder = order.items.find(i => i.productId === productId);
    if (existingInOrder) {
      // Restore/increase existing item qty
      setEditedQuantities(prev => ({ ...prev, [productId]: (prev[productId] || 0) + 1 }));
      return;
    }
    const existingNew = newItems.find(i => i.productId === productId);
    if (existingNew) {
      setNewItems(prev => prev.map(ni => ni.productId === productId ? { ...ni, quantity: ni.quantity + 1 } : ni));
      return;
    }
    setNewItems(prev => [...prev, {
      productId: product.id,
      name: product.name,
      nameAr: product.nameAr || '',
      price: product.onSale && product.salePrice ? product.salePrice : product.price,
      originalPrice: product.onSale && product.salePrice ? product.price : undefined,
      onSale: product.onSale && product.salePrice ? true : false,
      image: product.image || '',
      quantity: 1
    }]);
  };

  const saveEditedItems = async () => {
    setSavingItems(true);
    try {
      const existingItems = Object.entries(editedQuantities).map(([productId, quantity]) => ({
        productId, quantity: Math.max(0, quantity)
      }));
      const addedItems = newItems.filter(ni => ni.quantity > 0).map(ni => ({
        productId: ni.productId,
        name: ni.name,
        nameAr: ni.nameAr,
        price: ni.price,
        originalPrice: ni.originalPrice,
        onSale: ni.onSale,
        image: ni.image,
        quantity: ni.quantity,
        isNew: true
      }));
      const items = [...existingItems, ...addedItems];
      if (existingItems.every(i => i.quantity === 0) && addedItems.length === 0) {
        alert('Cannot remove all items from the order');
        setSavingItems(false);
        return;
      }
      const response = await fetch(`/api/orders/${id}/items`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ items })
      });
      if (response.ok) {
        const updated = await response.json();
        setOrder(updated);
        setEditingItems(false);
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to update items');
      }
    } catch (error) {
      alert('Failed to update items');
    } finally {
      setSavingItems(false);
    }
  };

  const startEditShipping = () => {
    setEditedShipping(order.shipping ?? 0);
    setEditedPayToDriver(!!order.payToDriver);
    setEditingShipping(true);
  };

  const saveEditedShipping = async () => {
    setSavingShipping(true);
    try {
      const response = await fetch(`/api/orders/${id}/shipping`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ shipping: editedShipping, payToDriver: editedPayToDriver })
      });
      if (response.ok) {
        const updated = await response.json();
        setOrder(updated);
        setEditingShipping(false);
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to update shipping');
      }
    } catch (error) {
      alert('Failed to update shipping');
    } finally {
      setSavingShipping(false);
    }
  };

  const EMIRATES = [
    { id: 'abu_dhabi', name: 'Abu Dhabi' },
    { id: 'al_ain', name: 'Al Ain' },
    { id: 'dubai', name: 'Dubai' },
    { id: 'sharjah', name: 'Sharjah' },
    { id: 'ajman', name: 'Ajman' },
    { id: 'umm_al_quwain', name: 'Umm Al Quwain' },
    { id: 'ras_al_khaimah', name: 'Ras Al Khaimah' },
    { id: 'fujairah', name: 'Fujairah' }
  ];

  const startEditAddress = () => {
    const addr = order.shippingAddress || {};
    setEditedAddress({
      fullName: addr.fullName || order.customerName || '',
      phone: addr.phone || order.customerPhone || '',
      address: addr.address || '',
      city: addr.city || '',
      emirate: addr.emirate || ''
    });
    setEditingAddress(true);
  };

  const saveEditedAddress = async () => {
    setSavingAddress(true);
    try {
      const response = await fetch(`/api/orders/${id}/address`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ shippingAddress: editedAddress })
      });
      if (response.ok) {
        const updated = await response.json();
        setOrder(updated);
        setEditingAddress(false);
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to update address');
      }
    } catch (error) {
      alert('Failed to update address');
    } finally {
      setSavingAddress(false);
    }
  };

  const copyPaymentId = () => {
    if (order?.paymentIntentId) {
      navigator.clipboard.writeText(order.paymentIntentId);
      haptic();
      setCopiedPaymentId(true);
      setTimeout(() => setCopiedPaymentId(false), 2000);
    }
  };

  const updateOrderStatus = async (newStatus, sendEmail = true) => {
    setUpdating(true);
    try {
      const response = await fetch(`/api/orders/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus, sendEmail })
      });

      if (response.ok) {
        setOrder(prev => ({ ...prev, status: newStatus }));
        setShowStatusMenu(false);
        setShowShipConfirm(false);
      }
    } catch (error) {
      console.error('Failed to update order status:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusClick = (status) => {
    updateOrderStatus(status, false);
  };

  const updatePaymentStatus = async (newStatus) => {
    setUpdatingPayment(true);
    try {
      const response = await fetch(`/api/orders/${id}/payment-status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ paymentStatus: newStatus })
      });

      if (response.ok) {
        const updatedOrder = await response.json();
        setOrder(updatedOrder);
        setShowPaymentStatusMenu(false);
      }
    } catch (error) {
      console.error('Failed to update payment status:', error);
    } finally {
      setUpdatingPayment(false);
    }
  };

  const toggleStatusMenu = () => {
    setShowStatusMenu(!showStatusMenu);
  };

  const checkRefundStatus = async () => {
    setCheckingRefund(true);
    try {
      const response = await fetch(`/api/payments/check-refund/${id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        // Refresh order to get updated payment status
        fetchOrder();
        alert(data.message + (data.refundAmount ? ` (${data.refundAmount} AED)` : ''));
      } else {
        alert(data.error || 'Failed to check refund status');
      }
    } catch (error) {
      console.error('Failed to check refund:', error);
      alert('Failed to check refund status');
    } finally {
      setCheckingRefund(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => <AedPrice amount={price} />;

  const formatEmirate = (emirate) => {
    if (!emirate) return '';
    // Convert abu_dhabi to Abu Dhabi, etc.
    return emirate.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getStatusClass = (status) => {
    const statusMap = {
      created: 'created',
      pending: 'warning',
      processing: 'shipped',
      shipped: 'shipped',
      delivered: 'success',
      cancelled: 'danger'
    };
    return statusMap[status] || 'secondary';
  };

  const getPaymentInfo = () => {
    const method = order?.paymentMethod || 'cod';
    const status = order?.paymentStatus || 'pending';

    if (method === 'card') {
      let statusLabel = t('pending');
      let className = 'payment-card-pending';

      if (status === 'paid') {
        statusLabel = t('paid');
        className = 'payment-card-paid';
      } else if (status === 'refunded') {
        statusLabel = t('refunded');
        className = 'payment-refunded';
      } else if (status === 'partially_refunded') {
        statusLabel = t('partiallyRefunded');
        className = 'payment-refunded';
      } else if (status === 'failed') {
        statusLabel = t('failed');
        className = 'payment-failed';
      }

      return {
        icon: <CreditCardIcon size="18px" />,
        label: t('creditDebitCard'),
        status: statusLabel,
        className: className,
        canCheckRefund: status === 'paid'
      };
    } else if (method === 'bank_transfer') {
      let statusLabel = t('pendingVerification');
      let className = 'payment-bank-pending';

      if (status === 'paid') {
        statusLabel = t('paid');
        className = 'payment-bank-verified';
      } else if (status === 'verified') {
        statusLabel = t('verified');
        className = 'payment-bank-verified';
      } else if (status === 'pending_verification' || status === 'pending') {
        statusLabel = t('pendingVerification');
        className = 'payment-bank-pending';
      }

      return {
        icon: <BankIcon size="18px" />,
        label: t('bankTransfer'),
        status: statusLabel,
        className: className,
        canCheckRefund: false,
        hasScreenshot: !!order?.transferScreenshot
      };
    } else {
      return {
        icon: <DollarIcon size="18px" />,
        label: t('cashOnDeliveryFull'),
        status: status === 'paid' ? t('collected') : t('pending'),
        className: status === 'paid' ? 'payment-cod-paid' : 'payment-cod-pending',
        canCheckRefund: false
      };
    }
  };

  if (loading) {
    return (
      <div className="admin-order-page">
        <div className="container">
          <div className="loading-box">
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="admin-order-page">
        <div className="container">
          <div className="not-found-box">
            <h2>{t('orderNotFound')}</h2>
            <Link to="/dashboard4/orders" className="btn btn-primary">{t('backToOrdersList')}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-order-page">
      <div className="container">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <Link to="/dashboard4/orders" className="back-link" style={{ marginBottom: 0 }}>{t('backToOrdersList')}</Link>
          {window.innerWidth >= 768 && (order.status === 'processing' || order.status === 'shipped') && (
            <button
              onClick={async () => {
                await updateOrderStatus('delivered');
                navigate('/dashboard4/orders');
              }}
              disabled={updating}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 20px',
                borderRadius: '50px',
                border: '1px solid #86efac',
                background: '#dcfce7',
                color: '#16a34a',
                fontSize: '14px',
                fontWeight: '700',
                cursor: updating ? 'not-allowed' : 'pointer',
                opacity: updating ? 0.6 : 1,
                transition: 'all 0.2s'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
              {updating ? (language === 'ar' ? 'جاري التحديث...' : 'Updating...') : (language === 'ar' ? 'انقر لتأكيد التوصيل' : 'Click to mark delivered')}
            </button>
          )}
        </div>

        {/* Header with Order ID and Status Capsule */}
        <div className="admin-order-header">
          <div className="header-left">
            <h1 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {language === 'ar' ? `طلب ${order.id}` : `Order ${order.id}`}
              <button
                onClick={() => {
                  navigator.clipboard.writeText(order.id);
                  haptic();
                  setCopiedOrderId(true);
                  setTimeout(() => setCopiedOrderId(false), 2000);
                }}
                style={{
                  background: copiedOrderId ? '#d1fae5' : '#f3f4f6',
                  border: '1px solid',
                  borderColor: copiedOrderId ? '#a7f3d0' : '#e5e7eb',
                  borderRadius: '6px',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '13px',
                  color: copiedOrderId ? '#065f46' : '#6b7280',
                  fontWeight: '500',
                  verticalAlign: 'middle'
                }}
                title={language === 'ar' ? 'نسخ رقم الطلب' : 'Copy Order ID'}
              >
                {copiedOrderId ? (
                  <><CheckIcon size="14px" /> {language === 'ar' ? 'تم' : 'Copied'}</>
                ) : (
                  <CopyIcon size="14px" />
                )}
              </button>
            </h1>
            <span className="order-date">{formatDate(order.createdAt)}</span>
          </div>
          <div className="header-right">
            <div className="status-capsule-wrapper" ref={statusMenuRef}>
              <span
                className={`status-capsule status-${getStatusClass(order.status)} ${updating ? 'updating' : ''}`}
                onClick={toggleStatusMenu}
              >
                {t(order.status)} ▼
              </span>

              {showStatusMenu && (
                <div className="status-dropdown-menu">
                  {['created', 'pending', 'processing', 'shipped', 'delivered', 'cancelled'].map(status => (
                    <div
                      key={status}
                      className={`status-menu-item status-${getStatusClass(status)} ${order.status === status ? 'active' : ''}`}
                      onClick={() => handleStatusClick(status)}
                    >
                      {t(status)}
                      {status === 'shipped' && order.status !== 'shipped' && <MailSendIcon size="14px" style={{ marginLeft: '4px', display: 'inline' }} />}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="admin-order-grid">
          {/* Customer Info */}
          <div className="info-card">
            <div className="card-header-with-action">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><UsersIcon size="18px" /> {t('customerInfo')}</h3>
              {!editingCustomer && (
                <button
                  className="copy-btn"
                  onClick={() => setEditingCustomer(true)}
                  style={{ fontSize: '12px' }}
                >
                  <EditIcon size="14px" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }} /> {t('edit')}
                </button>
              )}
            </div>
            <p className="customer-name">
              {order.customerName}
              {order.whatsappName && order.whatsappName !== order.customerName && (
                <span style={{ color: '#25D366', fontSize: '13px', fontWeight: '500', marginLeft: '8px' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="#25D366" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '3px', marginTop: '-1px' }}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  {order.whatsappName}
                </span>
              )}
            </p>

            {editingCustomer ? (
              <div style={{ marginTop: '10px' }}>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>{t('phone')}</label>
                  <input
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      borderRadius: '25px',
                      border: '1px solid #e5e7eb',
                      fontSize: '14px',
                      fontFamily: 'monospace',
                      boxSizing: 'border-box'
                    }}
                    placeholder={t('phone')}
                  />
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>{t('email')}</label>
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      borderRadius: '25px',
                      border: '1px solid #e5e7eb',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                    placeholder={t('email')}
                  />
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '12px', color: '#25D366', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    {language === 'ar' ? 'اسم الواتساب' : 'WhatsApp Name'}
                  </label>
                  <input
                    type="text"
                    value={whatsappName}
                    onChange={(e) => setWhatsappName(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      borderRadius: '25px',
                      border: '1px solid #e5e7eb',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                    placeholder={language === 'ar' ? 'الاسم في الواتساب' : 'Name on WhatsApp'}
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={saveCustomerInfo}
                    disabled={savingCustomer}
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '25px',
                      border: 'none',
                      background: 'var(--primary)',
                      color: 'white',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    {savingCustomer ? t('saving') : t('save')}
                  </button>
                  <button
                    onClick={() => {
                      setEditingCustomer(false);
                      setCustomerPhone(order.customerPhone || order.shippingAddress?.phone || '');
                      setCustomerEmail(order.customerEmail || '');
                      setWhatsappName(order.whatsappName || '');
                    }}
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '25px',
                      border: '1px solid #e5e7eb',
                      background: 'white',
                      color: '#374151',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    {t('cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {(order.customerPhone || order.shippingAddress?.phone) && (
                  <p style={{ color: '#374151', fontFamily: 'monospace', fontSize: '14px', margin: '4px 0' }}>
                    {order.customerPhone || order.shippingAddress?.phone}
                  </p>
                )}
                {order.customerEmail && (
                  <p style={{ color: '#6b7280', fontSize: '14px', margin: '4px 0' }}>
                    {order.customerEmail}
                  </p>
                )}
              </>
            )}
            <div className="customer-stats">
              <span className="order-count" onClick={() => setShowCustomerOrders(!showCustomerOrders)} style={{ cursor: 'pointer' }}>
                <PackageIcon size="14px" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }} /> {customerOrders.length} {customerOrders.length !== 1 ? t('ordersCount') : t('orderCount')} • {formatPrice(customerOrders.reduce((sum, o) => sum + (o.total || 0), 0))}
              </span>
              <span className="member-since">
                {t('memberSince')} {new Date(customerOrders.length > 0 ? (customerOrders[customerOrders.length - 1]?.createdAt || order.createdAt) : order.createdAt).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US', { month: 'short', year: 'numeric' })}
              </span>
            </div>
            {showCustomerOrders && customerOrders.length > 0 && (
              <div className="customer-orders-list">
                <h4>{t('orderHistory')} ({formatPrice(customerOrders.reduce((sum, o) => sum + (o.total || 0), 0))})</h4>
                {customerOrders.map((o) => (
                  <Link
                    key={o.id}
                    to={`/dashboard4/orders/${o.id}`}
                    className={`order-history-item ${o.id === order.id ? 'current' : ''}`}
                  >
                    <span>#{o.id.slice(0, 8)}</span>
                    <span>{Math.round(o.total)}</span>
                    <span>{new Date(o.createdAt).toLocaleDateString()}</span>
                    <span className={`mini-status ${o.status}`}>{t(o.status)}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Shipping Address */}
          <div className="info-card">
            <div className="card-header-with-action">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><TruckIcon size="18px" /> {language === 'ar' ? 'العنوان' : 'Address'}</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                {!editingAddress && ['created', 'pending', 'confirmed'].includes(order.status) && (
                  <button className="copy-btn" onClick={startEditAddress} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <EditIcon size="13px" /> {language === 'ar' ? 'تعديل' : 'Edit'}
                  </button>
                )}
                {!editingAddress && (
                  <>
                    <button className="copy-btn" onClick={copyAddress} title={t('copy')}>
                      {copied ? <><CheckIcon size="14px" style={{ display: 'inline-block', verticalAlign: 'middle' }} /> {t('copied')}</> : <><CopyIcon size="14px" style={{ display: 'inline-block', verticalAlign: 'middle' }} /> {t('copy')}</>}
                    </button>
                    <button className="copy-btn" onClick={printAddressLabel} title={t('print')} style={{ background: '#f3f4f6', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <PrinterIcon size="14px" /> {t('print')}
                    </button>
                  </>
                )}
                {editingAddress && (
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => setEditingAddress(false)} style={{ padding: '6px 14px', borderRadius: '20px', border: '1px solid #e2e8f0', background: 'white', color: '#64748b', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                      {language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </button>
                    <button onClick={saveEditedAddress} disabled={savingAddress} style={{ padding: '6px 14px', borderRadius: '20px', border: 'none', background: 'var(--primary)', color: 'white', fontSize: '13px', fontWeight: '600', cursor: 'pointer', opacity: savingAddress ? 0.6 : 1 }}>
                      {savingAddress ? '...' : (language === 'ar' ? 'حفظ' : 'Save')}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {!editingAddress ? (
              <>
                <p>{order.shippingAddress?.fullName}</p>
                <p>{order.shippingAddress?.phone}</p>
                <p>{order.shippingAddress?.address}</p>
                <p>{order.shippingAddress?.city}, {formatEmirate(order.shippingAddress?.emirate)}</p>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '5px' }}>
                <input type="text" className="form-input" placeholder={language === 'ar' ? 'الاسم الكامل' : 'Full Name'} value={editedAddress.fullName} onChange={e => setEditedAddress(prev => ({...prev, fullName: e.target.value}))} style={{ padding: '10px 14px', fontSize: '14px' }} />
                <input type="tel" className="form-input" placeholder={language === 'ar' ? 'الهاتف' : 'Phone'} value={editedAddress.phone} onChange={e => setEditedAddress(prev => ({...prev, phone: e.target.value}))} style={{ padding: '10px 14px', fontSize: '14px' }} />
                <input type="text" className="form-input" placeholder={language === 'ar' ? 'العنوان' : 'Address'} value={editedAddress.address} onChange={e => setEditedAddress(prev => ({...prev, address: e.target.value}))} style={{ padding: '10px 14px', fontSize: '14px' }} />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="text" className="form-input" placeholder={language === 'ar' ? 'المدينة' : 'City'} value={editedAddress.city} onChange={e => setEditedAddress(prev => ({...prev, city: e.target.value}))} style={{ padding: '10px 14px', fontSize: '14px', flex: 1 }} />
                  <select className="form-input" value={editedAddress.emirate} onChange={e => setEditedAddress(prev => ({...prev, emirate: e.target.value}))} style={{ padding: '10px 14px', fontSize: '14px', flex: 1 }}>
                    <option value="">{language === 'ar' ? 'الإمارة / المدينة' : 'Emirate / City'}</option>
                    {EMIRATES.map(em => <option key={em.id} value={em.id}>{em.name}</option>)}
                  </select>
                </div>
              </div>
            )}

            {/* WhatsApp button for created orders */}
            {(order.status === 'created' || order.status === 'pending') && (order.shippingAddress?.phone || order.customerPhone) && (
              <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
              <a
                href={`https://wa.me/${(() => {
                  let phone = (order.shippingAddress?.phone || order.customerPhone).replace(/[^0-9]/g, '');
                  // Convert UAE local format (05x) to international (9715x)
                  if (phone.startsWith('05') || phone.startsWith('5')) {
                    phone = '971' + (phone.startsWith('0') ? phone.slice(1) : phone);
                  }
                  return phone;
                })()}?text=${encodeURIComponent(
                  (() => {
                    const isDriverOrder = order.payToDriver && (order.shipping ?? 0) > 0;
                    const defaultTpl = `السلام عليكم،\n\nشكراً لطلبكم المنتجات الخالية من الغلوتين\n\nالاسم: {customerName}\nرقم الطلب: {orderId}\nالمبلغ الإجمالي: {total} درهم شامل التوصيل.\n\nيرجى إكمال الطلب عبر الرابط التالي:\n{paymentLink}`;
                    const defaultDriverTpl = `السلام عليكم،\n\nشكراً لطلبكم المنتجات الخالية من الغلوتين\n\nالاسم: {customerName}\nرقم الطلب: {orderId}\nالمبلغ الإجمالي: {total} درهم\n\nرسوم التوصيل تُحدد حسب المنطقة وتُدفع نقداً للسائق عند الاستلام\nDelivery fee is based on your area and paid cash to the driver\n\nيرجى إكمال الطلب عبر الرابط التالي:\n{paymentLink}`;
                    const tpl = isDriverOrder
                      ? (whatsappDriverTemplate || defaultDriverTpl)
                      : (whatsappTemplate || defaultTpl);
                    return tpl
                      .replace(/\{customerName\}/g, order.customerName || '')
                      .replace(/\{orderId\}/g, order.id)
                      .replace(/\{total\}/g, String(Math.round(order.total)))
                      .replace(/\{deliveryFee\}/g, String(Math.round(order.shipping ?? 0)))
                      .replace(/\{paymentLink\}/g, `https://web.ae/pay/${order.id}`);
                  })()
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="whatsapp-btn"
                style={{
                  flex: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  background: '#25D366',
                  color: 'white',
                  borderRadius: '25px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '13px'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                {language === 'ar' ? 'واتساب' : 'WhatsApp'}
              </a>
              <button
                onClick={copyPaymentLink}
                style={{
                  flex: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '10px 16px',
                  background: copiedLink ? '#10b981' : '#f3f4f6',
                  color: copiedLink ? 'white' : '#374151',
                  borderRadius: '25px',
                  border: 'none',
                  fontWeight: '600',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                {copiedLink ? (language === 'ar' ? '✓ تم النسخ' : '✓ Copied') : <><LinkIcon size="14px" style={{ display: 'inline-block', verticalAlign: 'middle' }} /> {language === 'ar' ? 'نسخ الرابط' : 'Copy Link'}</>}
              </button>
              </div>
            )}
            {(order.status === 'created' || order.status === 'pending') && (order.shippingAddress?.phone || order.customerPhone) && (
              <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px', textAlign: 'center' }}>
                {language === 'ar'
                  ? 'سيتم إرسال رسالة للعميل لإتمام الطلب'
                  : 'Will send a message asking customer to complete the order'}
              </p>
            )}

            {/* Printed toggle */}
            <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>Shipping Label:</span>
              <button
                onClick={async () => {
                  try {
                    const response = await fetch(`/api/orders/${order.id}/printed`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                    });
                    if (response.ok) {
                      const updated = await response.json();
                      setOrder(prev => ({ ...prev, printed: updated.printed, status: updated.status }));
                    }
                  } catch (err) { console.error(err); }
                }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '6px 16px', borderRadius: '20px', border: order.printed ? '1.5px solid #f59e0b' : '1.5px solid #d1d5db', cursor: 'pointer',
                  fontSize: '13px', fontWeight: '600',
                  background: order.printed ? '#fffbeb' : 'white',
                  color: order.printed ? '#b45309' : '#9ca3af'
                }}
              >
                {order.printed ? '✓ Printed' : '✕ Not Printed'}
              </button>
            </div>
          </div>

          {/* Payment Info */}
          <div className="info-card">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><CreditCardIcon size="18px" /> {t('payment')}</h3>
            {(() => {
              const payment = getPaymentInfo();
              return (
                <>
                  <div className={`payment-info-box ${payment.className}`}>
                    <div className="payment-method">
                      <span className="payment-icon">{payment.icon}</span>
                      <span className="payment-label">{payment.label}</span>
                    </div>
                    {order.paymentMethod === 'bank_transfer' ? (
                      <button
                        className={`payment-status-badge clickable ${order.paymentStatus === 'paid' ? 'paid' : 'pending'}`}
                        onClick={() => {
                          const newStatus = order.paymentStatus === 'paid' ? 'pending_verification' : 'paid';
                          console.log('Updating payment status to:', newStatus);
                          updatePaymentStatus(newStatus);
                        }}
                        disabled={updatingPayment}
                        style={{ cursor: 'pointer', opacity: updatingPayment ? 0.6 : 1, border: 'none' }}
                      >
                        {updatingPayment ? '...' : (order.paymentStatus === 'paid' ? <><CheckCircleIcon size="14px" style={{ display: 'inline-block', verticalAlign: 'middle' }} /> {t('paid')}</> : <><ClockIcon size="14px" style={{ display: 'inline-block', verticalAlign: 'middle' }} /> {t('pending')}</>)}
                      </button>
                    ) : (
                      <div className={`payment-status-badge ${payment.status === 'Paid' || payment.status === 'Collected' || payment.status === 'Verified' ? 'paid' : payment.status === 'Refunded' || payment.status === 'Partially Refunded' ? 'refunded' : 'pending'}`}>
                        {payment.status}
                      </div>
                    )}
                  </div>
                  {order.paymentMethod === 'bank_transfer' && order.updatedAt && (
                    <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '8px' }}>
                      {t('lastUpdated')}: {formatDate(order.updatedAt)}
                    </div>
                  )}
                  {payment.canCheckRefund && (
                    <button
                      className="check-refund-btn"
                      onClick={checkRefundStatus}
                      disabled={checkingRefund}
                    >
                      {checkingRefund ? t('checking') : t('checkRefundStatus')}
                    </button>
                  )}
                  {/* Bank Transfer Screenshot */}
                  {payment.hasScreenshot && order.transferScreenshot && (
                    <div className="transfer-screenshot-section">
                      <h4 style={{ margin: '15px 0 10px', fontSize: '14px', color: 'var(--gray-600)' }}>
                        {t('transferReceipt')}
                      </h4>
                      <div
                        className="screenshot-preview"
                        onClick={() => setShowScreenshotModal(true)}
                        style={{
                          cursor: 'pointer',
                          border: '1px solid var(--gray-200)',
                          borderRadius: '8px',
                          overflow: 'hidden'
                        }}
                      >
                        <img
                          src={order.transferScreenshot}
                          alt="Transfer Receipt"
                          style={{
                            width: '100%',
                            maxHeight: '200px',
                            objectFit: 'cover'
                          }}
                        />
                        <div style={{
                          padding: '10px 12px',
                          background: '#1e293b',
                          textAlign: 'center',
                          fontSize: '15px',
                          color: '#fff',
                          fontWeight: '700'
                        }}>
                          Total of this order is: {formatPrice(order.total)}
                        </div>
                        <div style={{
                          padding: '6px',
                          background: 'var(--gray-50)',
                          textAlign: 'center',
                          fontSize: '11px',
                          color: 'var(--gray-500)'
                        }}>
                          {t('clickToViewFullSize')}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
            {order.paymentIntentId && order.paymentMethod === 'card' && (
              <div className="payment-id-row">
                <span className="payment-id-label">{t('paymentId')}:</span>
                <span className="payment-id-value">{order.paymentIntentId}</span>
                <button className="copy-payment-btn" onClick={copyPaymentId} title="Copy Payment ID">
                  {copiedPaymentId ? <CheckIcon size="14px" /> : <CopyIcon size="14px" />}
                </button>
              </div>
            )}
          </div>

          {/* Notes Card */}
          <div className="info-card notes-card">
            <div className="card-header-with-action">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><EditIcon size="18px" /> {language === 'ar' ? 'ملاحظة' : 'Note'}</h3>
              {!editingNote && (
                <button
                  className="copy-btn"
                  onClick={() => setEditingNote(true)}
                  style={{ fontSize: '12px' }}
                >
                  <EditIcon size="14px" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }} /> {t('edit')}
                </button>
              )}
            </div>

            {editingNote ? (
              <div>
                <textarea
                  value={orderNote}
                  onChange={(e) => setOrderNote(e.target.value)}
                  placeholder={language === 'ar' ? 'أضف ملاحظة...' : 'Add a note...'}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '20px',
                    border: '1px solid var(--gray-200)',
                    fontSize: '14px',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    marginBottom: '12px',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={saveNote}
                    disabled={savingNote}
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '25px',
                      border: 'none',
                      background: 'var(--primary)',
                      color: 'white',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    {savingNote ? '...' : t('save')}
                  </button>
                  <button
                    onClick={() => {
                      setEditingNote(false);
                      // Reset to original value
                      if (order.adminNotes) {
                        try {
                          const noteData = JSON.parse(order.adminNotes);
                          if (Array.isArray(noteData) && noteData.length > 0) {
                            setOrderNote(noteData[noteData.length - 1].text || '');
                          } else if (noteData.text) {
                            setOrderNote(noteData.text);
                          }
                        } catch (e) {
                          setOrderNote('');
                        }
                      } else {
                        setOrderNote('');
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '25px',
                      border: '1px solid var(--gray-200)',
                      background: 'white',
                      color: '#374151',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    {t('cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {orderNote ? (
                  <p style={{
                    margin: '0',
                    color: 'var(--gray-800)',
                    fontSize: '14px',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap'
                  }}>
                    {orderNote}
                  </p>
                ) : (
                  <p style={{
                    margin: '0',
                    color: 'var(--gray-400)',
                    fontSize: '14px',
                    fontStyle: 'italic'
                  }}>
                    {language === 'ar' ? 'لا توجد ملاحظات' : 'No note'}
                  </p>
                )}
                {noteLastUpdated && (
                  <div style={{
                    marginTop: '10px',
                    fontSize: '11px',
                    color: 'var(--gray-400)'
                  }}>
                    {language === 'ar' ? 'آخر تحديث: ' : 'Last updated: '}
                    {new Date(noteLastUpdated).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                    {noteUpdatedBy && ` - ${noteUpdatedBy}`}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Screenshot Modal */}
        {showScreenshotModal && order.transferScreenshot && (
          <div
            className="screenshot-modal-overlay"
            onClick={() => setShowScreenshotModal(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px'
            }}
          >
            <div
              className="screenshot-modal-content"
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '90%',
                maxHeight: '90%',
                position: 'relative'
              }}
            >
              <button
                onClick={() => setShowScreenshotModal(false)}
                style={{
                  position: 'absolute',
                  top: '-40px',
                  right: '0',
                  background: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  fontSize: '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
              <div style={{
                padding: '12px 20px',
                background: '#1e293b',
                borderRadius: '8px 8px 0 0',
                textAlign: 'center',
                fontSize: '18px',
                color: '#fff',
                fontWeight: '700'
              }}>
                Total of this order is: {formatPrice(order.total)}
              </div>
              <img
                src={order.transferScreenshot}
                alt="Transfer Receipt"
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  borderRadius: '0 0 8px 8px'
                }}
              />
              <a
                href={order.transferScreenshot}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'block',
                  textAlign: 'center',
                  marginTop: '15px',
                  color: 'white',
                  textDecoration: 'none',
                  fontSize: '14px'
                }}
              >
                {t('openInNewTab')}
              </a>
            </div>
          </div>
        )}

        {/* Order Items */}
        <div className="items-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><PackageIcon size="18px" /> {t('orderItems')}</h3>
            {!editingItems && (
              <button onClick={startEditItems} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 14px', borderRadius: '20px', border: '1px solid #e2e8f0', background: '#f8fafc', color: '#475569', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                <EditIcon size="13px" /> {language === 'ar' ? 'تعديل' : 'Edit'}
              </button>
            )}
            {editingItems && (
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={() => setEditingItems(false)} style={{ padding: '6px 14px', borderRadius: '20px', border: '1px solid #e2e8f0', background: 'white', color: '#64748b', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </button>
                <button onClick={saveEditedItems} disabled={savingItems} style={{ padding: '6px 14px', borderRadius: '20px', border: 'none', background: 'var(--primary)', color: 'white', fontSize: '13px', fontWeight: '600', cursor: 'pointer', opacity: savingItems ? 0.6 : 1 }}>
                  {savingItems ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...') : (language === 'ar' ? 'حفظ' : 'Save')}
                </button>
              </div>
            )}
          </div>
          <table className="items-table">
            <thead>
              <tr>
                <th>{t('product')}</th>
                <th>{t('price')}</th>
                <th>{t('qty')}</th>
                <th>{t('total')}</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((item, index) => {
                const currentQty = editingItems ? (editedQuantities[item.productId] ?? item.quantity) : item.quantity;
                const isRemoved = editingItems && currentQty === 0;
                return (
                <tr key={index} style={{ opacity: isRemoved ? 0.35 : 1, textDecoration: isRemoved ? 'line-through' : 'none' }}>
                  <td>
                    <div className="product-cell">
                      <img src={item.image} alt={item.name} />
                      <span>{item.name}</span>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {formatPrice(item.price)}
                      {item.onSale && item.originalPrice && item.originalPrice !== item.price && (
                        <span style={{ fontWeight: '700', color: '#dc2626', fontSize: '11px' }}>
                          -{Math.round((1 - item.price / item.originalPrice) * 100)}%
                        </span>
                      )}
                    </div>
                    {item.onSale && item.originalPrice && item.originalPrice !== item.price && (
                      <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                        <span style={{ textDecoration: 'line-through' }}>{formatPrice(item.originalPrice)}</span>
                      </div>
                    )}
                  </td>
                  <td>
                    {editingItems ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {currentQty > 0 ? (
                          <button onClick={() => changeEditQty(item.productId, -1)} style={{ width: '28px', height: '28px', borderRadius: '50%', border: '1px solid #d1d5db', background: 'white', fontSize: '16px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: currentQty === 1 ? '#ef4444' : '#64748b' }}>{currentQty === 1 ? '✕' : '−'}</button>
                        ) : (
                          <button onClick={() => changeEditQty(item.productId, 1)} style={{ width: '28px', height: '28px', borderRadius: '50%', border: '1px solid #d1d5db', background: '#dcfce7', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#16a34a' }}>↩</button>
                        )}
                        <span style={{ minWidth: '24px', textAlign: 'center', fontWeight: '700', fontSize: '15px', color: isRemoved ? '#ef4444' : 'inherit' }}>{isRemoved ? '0' : currentQty}</span>
                        {currentQty > 0 && (
                          <button onClick={() => changeEditQty(item.productId, 1)} style={{ width: '28px', height: '28px', borderRadius: '50%', border: '1px solid #d1d5db', background: 'white', fontSize: '16px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>+</button>
                        )}
                      </div>
                    ) : (
                      currentQty
                    )}
                  </td>
                  <td>
                    {formatPrice(item.price * currentQty)}
                    {item.onSale && item.originalPrice && item.originalPrice !== item.price && (
                      <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                        <span style={{ textDecoration: 'line-through' }}>{formatPrice(item.originalPrice * currentQty)}</span>
                      </div>
                    )}
                  </td>
                </tr>
                );
              })}
              {/* New items added from product list */}
              {editingItems && newItems.map((item, index) => (
                <tr key={`new-${index}`} style={{ background: '#f0fdf4' }}>
                  <td>
                    <div className="product-cell">
                      {item.image && <img src={item.image} alt={item.name} />}
                      <span style={{ color: '#16a34a', fontWeight: '600' }}>{item.name} <span style={{ fontSize: '10px', background: '#dcfce7', padding: '1px 6px', borderRadius: '8px', fontWeight: '700' }}>NEW</span></span>
                    </div>
                  </td>
                  <td>{formatPrice(item.price)}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <button onClick={() => changeEditQty(item.productId, -1)} style={{ width: '28px', height: '28px', borderRadius: '50%', border: '1px solid #d1d5db', background: 'white', fontSize: '16px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: item.quantity === 1 ? '#ef4444' : '#64748b' }}>{item.quantity === 1 ? '✕' : '−'}</button>
                      <span style={{ minWidth: '24px', textAlign: 'center', fontWeight: '700', fontSize: '15px' }}>{item.quantity}</span>
                      <button onClick={() => changeEditQty(item.productId, 1)} style={{ width: '28px', height: '28px', borderRadius: '50%', border: '1px solid #d1d5db', background: 'white', fontSize: '16px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>+</button>
                    </div>
                  </td>
                  <td>{formatPrice(item.price * item.quantity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Add product dropdown in edit mode */}
          {editingItems && allProducts.length > 0 && (
            <div style={{ padding: '12px 0', borderTop: '1px dashed #e2e8f0', marginTop: '4px' }}>
              <select
                onChange={(e) => { addNewItem(e.target.value); e.target.value = ''; }}
                defaultValue=""
                style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '14px', color: '#475569', background: '#f8fafc', cursor: 'pointer' }}
              >
                <option value="" disabled>{language === 'ar' ? '+ إضافة منتج...' : '+ Add product...'}</option>
                {allProducts
                  .filter(p => !order.items.some(i => i.productId === p.id) && !newItems.some(ni => ni.productId === p.id))
                  .map(p => (
                    <option key={p.id} value={p.id}>{p.name} — AED {p.onSale && p.salePrice ? p.salePrice : p.price}</option>
                  ))
                }
              </select>
            </div>
          )}
        </div>

        {/* Order Summary */}
        <div className="summary-card">
          <div className="summary-row">
            <span>{t('subtotal')}</span>
            <span>{formatPrice(order.subtotal)}</span>
          </div>
          {order.discount > 0 && (
            <div className="summary-row" style={{ color: '#059669' }}>
              <span>{t('discount')} {order.promoCode && <span style={{ fontSize: '11px', background: '#d1fae5', padding: '2px 6px', borderRadius: '4px', fontWeight: '600' }}>{order.promoCode}</span>}</span>
              <span>-{formatPrice(order.discount)}</span>
            </div>
          )}

          {/* Shipping - editable card when created */}
          {order.status === 'created' && deliveryFeeOptions.length > 0 ? (
            <div style={{
              margin: '10px 0',
              padding: '12px 14px',
              borderRadius: '12px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: editingShipping ? '10px' : '0' }}>
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#475569' }}>{t('shipping')}</span>
                {!editingShipping ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {order.payToDriver && (order.shipping ?? 0) > 0 ? (
                      <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', background: '#fffbeb', color: '#b45309', fontWeight: '600', border: '1px solid #fcd34d' }}>
                        🚚 {(order.shipping ?? 0) === 1 ? 'Pay to Driver (based on area)' : `Pay to Driver AED ${Math.round(order.shipping)}`}
                      </span>
                    ) : (
                      <span style={{ fontWeight: '700', color: (order.shipping ?? 0) === 0 ? '#22c55e' : '#1e293b' }}>
                        {(order.shipping ?? 0) === 0 ? 'Free' : formatPrice(order.shipping)}
                      </span>
                    )}
                    <button onClick={startEditShipping} style={{ padding: '3px 10px', borderRadius: '12px', border: '1px solid #d1d5db', background: 'white', color: '#64748b', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>
                      {language === 'ar' ? 'تغيير' : 'Change'}
                    </button>
                  </div>
                ) : (
                  editedPayToDriver && editedShipping > 0 ? (
                    <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', background: '#fffbeb', color: '#b45309', fontWeight: '600', border: '1px solid #fcd34d' }}>
                      🚚 {editedShipping === 1 ? 'Pay to Driver (based on area)' : `Pay to Driver AED ${Math.round(editedShipping)}`}
                    </span>
                  ) : (
                    <span style={{ fontWeight: '700', color: editedShipping === 0 ? '#22c55e' : '#1e293b' }}>
                      {editedShipping === 0 ? 'Free' : formatPrice(editedShipping)}
                    </span>
                  )
                )}
              </div>
              {editingShipping && (
                <>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    {deliveryFeeOptions.map((fee, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => { setEditedShipping(fee); if (fee === 0) setEditedPayToDriver(false); if (fee === 1) setEditedPayToDriver(true); }}
                        style={{
                          padding: '6px 14px',
                          borderRadius: '16px',
                          border: editedShipping === fee ? '2px solid var(--primary)' : '1px solid #d1d5db',
                          background: editedShipping === fee ? '#eff6ff' : 'white',
                          color: editedShipping === fee ? 'var(--primary)' : '#64748b',
                          fontWeight: editedShipping === fee ? '700' : '500',
                          fontSize: '13px',
                          cursor: 'pointer'
                        }}
                      >
                        {fee === 0 ? 'Free' : fee === 1 ? '🚚 Pay to Driver' : `AED ${fee}`}
                      </button>
                    ))}
                  </div>
                  {editedShipping > 1 && (
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      padding: '8px 10px',
                      borderRadius: '10px',
                      background: editedPayToDriver ? '#fffbeb' : 'transparent',
                      border: editedPayToDriver ? '1px solid #fcd34d' : '1px solid transparent',
                      marginBottom: '10px'
                    }}>
                      <input
                        type="checkbox"
                        checked={editedPayToDriver}
                        onChange={(e) => setEditedPayToDriver(e.target.checked)}
                        style={{ width: '16px', height: '16px', accentColor: '#f59e0b', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '13px', fontWeight: '600', color: editedPayToDriver ? '#b45309' : '#64748b' }}>
                        Pay to driver on delivery
                      </span>
                    </label>
                  )}
                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                    <button onClick={() => setEditingShipping(false)} style={{ padding: '5px 14px', borderRadius: '16px', border: '1px solid #e2e8f0', background: 'white', color: '#64748b', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                      {language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </button>
                    <button onClick={saveEditedShipping} disabled={savingShipping} style={{ padding: '5px 14px', borderRadius: '16px', border: 'none', background: 'var(--primary)', color: 'white', fontSize: '12px', fontWeight: '600', cursor: 'pointer', opacity: savingShipping ? 0.6 : 1 }}>
                      {savingShipping ? '...' : (language === 'ar' ? 'حفظ' : 'Save')}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="summary-row">
              <span>{t('shipping')}</span>
              {order.payToDriver && (order.shipping ?? 0) > 0 ? (
                <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', background: '#fffbeb', color: '#b45309', fontWeight: '600', border: '1px solid #fcd34d' }}>
                  🚚 {(order.shipping ?? 0) === 1 ? 'Pay to Driver (based on area)' : `Pay to Driver AED ${Math.round(order.shipping)}`}
                </span>
              ) : (
                <span>{formatPrice(order.shipping ?? 0)}</span>
              )}
            </div>
          )}

          {order.payToDriver && (order.shipping ?? 0) > 0 && (
            <div style={{
              margin: '8px 0',
              padding: '10px 14px',
              borderRadius: '10px',
              background: '#fffbeb',
              border: '1px solid #fcd34d',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              color: '#92400e'
            }}>
              <span style={{ fontSize: '16px' }}>🚚</span>
              <span>
                {(order.shipping ?? 0) === 1
                  ? 'Delivery fee based on customer area — paid to driver on delivery'
                  : `Customer will pay AED ${Math.round(order.shipping)} to driver on delivery`}
              </span>
            </div>
          )}

          <div className="summary-row total">
            <span>{t('total')}</span>
            <span>{formatPrice(order.total)}</span>
          </div>
        </div>

        {/* Delete Order & Pay Page Buttons */}
        <div style={{ marginTop: '20px', textAlign: 'center', display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={() => window.open(`/pay/${order.id}`, '_blank')}
            style={{
              padding: '12px 32px',
              borderRadius: '25px',
              border: '1px solid #bfdbfe',
              background: '#eff6ff',
              color: '#2563eb',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            {language === 'ar' ? 'صفحة الدفع' : 'Pay Page'} ↗
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            style={{
              padding: '12px 32px',
              borderRadius: '25px',
              border: '1px solid #fecaca',
              background: '#fef2f2',
              color: '#dc2626',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            {t('deleteOrder')}
          </button>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div
            className="modal-overlay"
            onClick={() => setShowDeleteConfirm(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px'
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '16px',
                padding: '24px',
                maxWidth: '400px',
                width: '100%'
              }}
            >
              <h3 style={{ margin: '0 0 16px', fontSize: '18px', color: '#dc2626' }}>
                {t('deleteOrder')}
              </h3>
              <p style={{ color: 'var(--gray-600)', marginBottom: '20px' }}>
                {t('confirmDeleteOrder')}
              </p>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                  style={{ flex: 1 }}
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={handleDeleteOrder}
                  disabled={deleting}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '25px',
                    border: 'none',
                    background: '#dc2626',
                    color: 'white',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '14px',
                    opacity: deleting ? 0.6 : 1
                  }}
                >
                  {deleting ? t('deleting') : t('deleteOrder')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Ship Confirmation Modal */}
      </div>
    </div>
  );
};

export default AdminOrderDetail;
